import { ThemeKey } from "@smartsheet/design-tokens";
import { color } from "@smartsheet/design-tokens-react";
import {
    PaginationArrowLeftIcon,
    PaginationArrowRightIcon,
    PaginationFirstPageIcon,
    PaginationLastPageIcon,
} from "@smartsheet/lodestar-icons";
import { getClientId } from "@smartsheet/lodestar-utils";
import { themed } from "@smartsheet/sds-theme-internal";
import { ChevronLeftIcon, ChevronLeftLineIcon, ChevronRightIcon, ChevronRightLineIcon } from "@smartsheet/ursa-icons";
import React, { forwardRef, useEffect } from "react";
import { useI18n } from "../../../i18n/i18n";
import { useTheme } from "../../../theme/utils/useTheme";
import { LiveRegion, useLiveRegion } from "../../liveregion";
import { paginationTheme } from "../paginationTheme";
import { Container } from "../styled";
import { StyledIconButton } from "../styled/StyledIconButton";
import { StyledTypeRamp } from "../styled/StyledTypeRamp";
import { PaginationProps } from "../types";

type PaginationBaseProps = Omit<PaginationProps, "locale">;

export const PaginationBase = forwardRef<HTMLDivElement, PaginationBaseProps>(
    (
        {
            className,
            clientId,
            entitiesPerPage,
            from,
            includeFirstAndLastPage = false,
            onFirstPageClick,
            onLastPageClick,
            onNextClick,
            onPrevClick,
            total,
            variant = "inline",
            ...rest
        },
        ref
    ) => {
        if (process.env.NODE_ENV !== "production") {
            if (entitiesPerPage < 1) {
                console.warn("Pagination: `entitiesPerPage` cannot be less than 1.");
            }
            if (total < 1) {
                console.warn(`Pagination: \`total\` (${total}) cannot be less than 1.`);
            }
            if (from < 1) {
                console.warn(`Pagination: \`from\` (${from}) cannot be less than 1.`);
            }
        }

        const { key } = useTheme();
        const themeMode = key as ThemeKey;
        const { i18n } = useI18n();
        const cannotGoBack = from === 1;
        const cannotGoForward = from + entitiesPerPage > total;
        const to = Math.min(from + entitiesPerPage - 1, total);

        const { getLiveRegionProps, speak } = useLiveRegion();

        const icons = themed({
            firstPage: {
                "lodestar-light": (
                    <PaginationFirstPageIcon
                        color={
                            cannotGoBack
                                ? color("color--icons--neutral--strong")
                                : color("color--text--neutral--strong")
                        }
                    />
                ),
                "ursa-light": (
                    <ChevronLeftLineIcon
                        size={20}
                        color={
                            cannotGoBack
                                ? color("color--icons--neutral--weaker")
                                : color("color--text--neutral--strong")
                        }
                    />
                ),
            },
            previousPage: {
                "lodestar-light": (
                    <PaginationArrowLeftIcon
                        color={
                            cannotGoBack
                                ? color("color--icons--neutral--strong")
                                : color("color--text--neutral--strong")
                        }
                    />
                ),
                "ursa-light": (
                    <ChevronLeftIcon
                        size={20}
                        color={
                            cannotGoBack
                                ? color("color--icons--neutral--weaker")
                                : color("color--text--neutral--strong")
                        }
                    />
                ),
            },
            nextPage: {
                "lodestar-light": (
                    <PaginationArrowRightIcon
                        color={
                            cannotGoForward
                                ? color("color--icons--neutral--strong")
                                : color("color--text--neutral--strong")
                        }
                    />
                ),
                "ursa-light": (
                    <ChevronRightIcon
                        size={20}
                        color={
                            cannotGoForward
                                ? color("color--icons--neutral--weaker")
                                : color("color--text--neutral--strong")
                        }
                    />
                ),
            },
            lastPage: {
                "lodestar-light": (
                    <PaginationLastPageIcon
                        color={
                            cannotGoForward
                                ? color("color--icons--neutral--strong")
                                : color("color--text--neutral--strong")
                        }
                    />
                ),
                "ursa-light": (
                    <ChevronRightLineIcon
                        size={20}
                        color={
                            cannotGoForward
                                ? color("color--icons--neutral--weaker")
                                : color("color--text--neutral--strong")
                        }
                    />
                ),
            },
        });

        useEffect(() => {
            speak(
                i18n._(
                    /* i18n */ {
                        comment:
                            "This string is read to a screen reader user using a live region when the pagination content changes",
                        id: "{from} through {to} of {total}",
                        values: {
                            from,
                            to,
                            total,
                        },
                    }
                )
            );
        }, [speak, from, i18n, to, total]);

        return (
            <Container
                orientation="row"
                space={paginationTheme.container.space(themeMode)}
                clientId={clientId}
                ref={ref}
                variant={variant}
                className={className}
                role="group"
                aria-label={i18n._(
                    /* i18n */ {
                        comment:
                            "This aria label is read to the user when the screen reader focuses on the container of the pagination component",
                        id: "Pagination, showing {from} through {to} of {total} entities",
                        values: {
                            from,
                            to,
                            total,
                        },
                    }
                )}
                themeMode={themeMode}
                {...rest}
            >
                {includeFirstAndLastPage ? (
                    <React.Fragment>
                        <StyledIconButton
                            appearance={paginationTheme.iconButton.appearance(themeMode)}
                            clientId={getClientId(clientId, "first")}
                            size={paginationTheme.iconButton.size(themeMode)}
                            onClick={onFirstPageClick}
                            aria-label={i18n._(
                                /* i18n */ {
                                    comment:
                                        "This aria label is read to the user when the screen reader focuses on the first page button",
                                    id: "Go to the first page",
                                }
                            )}
                            isDisabled={cannotGoBack}
                            iconBefore={icons.firstPage(themeMode)}
                            themeMode={themeMode}
                            disabled={cannotGoBack} // necessary for HTML disabled attribute for a11y
                        />
                    </React.Fragment>
                ) : null}
                <StyledIconButton
                    appearance={paginationTheme.iconButton.appearance(themeMode)}
                    clientId={getClientId(clientId, "prev")}
                    size="medium"
                    onClick={onPrevClick}
                    aria-label={i18n._(
                        /* i18n */ {
                            comment:
                                "This aria label is read to the user when the screen reader focuses on the previous button",
                            id: "Show previous {entitiesPerPage} entities",
                            values: {
                                entitiesPerPage,
                            },
                        }
                    )}
                    isDisabled={cannotGoBack}
                    disabled={cannotGoBack} // necessary for HTML disabled attribute for a11y
                    iconBefore={icons.previousPage(themeMode)}
                    themeMode={themeMode}
                />
                <StyledTypeRamp
                    // @ts-expect-error TODO: update TypeRampV2 to accept themeMode directly
                    system={themeMode.split("-")[0]}
                    themeMode={themeMode}
                    variant={paginationTheme.text.variant(themeMode)}
                    aria-hidden={true}
                >
                    {i18n._(
                        /* i18n */ {
                            comment:
                                "This is the current range of entities that the user is viewing out of a total number of entities",
                            id: "{from}-{to} of {total}",
                            values: {
                                from,
                                to,
                                total,
                            },
                        }
                    )}
                </StyledTypeRamp>
                <StyledIconButton
                    appearance={paginationTheme.iconButton.appearance(themeMode)}
                    clientId={getClientId(clientId, "next")}
                    size="medium"
                    onClick={onNextClick}
                    aria-label={i18n._(
                        /* i18n */ {
                            comment:
                                "This aria label is read to the user when the screen reader focuses on the next button",
                            id: "Show next {entitiesPerPage} entities",
                            values: {
                                entitiesPerPage,
                            },
                        }
                    )}
                    isDisabled={cannotGoForward}
                    disabled={cannotGoForward} // necessary for HTML disabled attribute for a11y
                    iconBefore={icons.nextPage(themeMode)}
                    themeMode={themeMode}
                />
                {includeFirstAndLastPage ? (
                    <React.Fragment>
                        <StyledIconButton
                            appearance={paginationTheme.iconButton.appearance(themeMode)}
                            clientId={getClientId(clientId, "last")}
                            size="medium"
                            onClick={onLastPageClick}
                            aria-label={i18n._(
                                /* i18n */ {
                                    comment:
                                        "This aria label is read to the user when the screen reader focuses on the last page button",
                                    id: "Go to the last page",
                                }
                            )}
                            isDisabled={cannotGoForward}
                            disabled={cannotGoForward} // necessary for HTML disabled attribute for a11y
                            iconBefore={icons.lastPage(themeMode)}
                            themeMode={themeMode}
                        />
                    </React.Fragment>
                ) : null}
                <LiveRegion {...getLiveRegionProps()} />
            </Container>
        );
    }
);

PaginationBase.displayName = "PaginationBase";
